import { motion } from 'framer-motion';
import {
  BarChart3,
  Activity,
  TrendingUp,
} from 'lucide-react';
import { KpiSummaryCards } from '../components/kpi/KpiSummaryCards';
import { LatencyDashboard } from '../components/kpi/LatencyDashboard';
import { CostSimulation } from '../components/kpi/CostSimulation';
import { DeflectionAnalytics } from '../components/kpi/DeflectionAnalytics';
import { SystemPerformance } from '../components/kpi/SystemPerformance';
import { MetricsStream } from '../components/kpi/MetricsStream';
import { useMetricsData } from '../hooks/useMetricsData';
import { pageTransition, staggerContainer, fadeUpItem } from '../hooks/useAnimation';

export function Analytics() {
  const {
    sttLatency,
    agentLatency,
    ttsLatency,
    callEvents,
    deflectionTrend,
    issueDeflection,
    funnel,
    system,
  } = useMetricsData(2000);

  return (
    <motion.div
      variants={pageTransition}
      initial="initial"
      animate="animate"
      exit="exit"
      className="p-6 space-y-6"
    >
      {/* ── Page Header ─────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-vokentra" />
            Analytics Dashboard
          </h1>
          <p className="text-sm text-text-muted mt-1">
            Investor-grade KPI analytics for Vokentra UK AI Voice Contact Center
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Real-time indicator */}
          <motion.div
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-success/5 border border-success/10"
            animate={{ opacity: [1, 0.7, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <span className="w-2 h-2 rounded-full bg-success animate-pulse-fast" />
            <span className="text-xs text-success font-medium">Real-Time Data</span>
          </motion.div>
          {/* Last updated */}
          <div className="text-xs text-text-muted tabular-nums">
            Updated: {new Date().toLocaleTimeString('en-GB', {
              timeZone: 'Europe/London',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: false,
            })}
          </div>
        </div>
      </motion.div>

      {/* ── Section 1: KPI Summary Cards ────────────────────────────── */}
      <motion.section
        variants={staggerContainer(0.1)}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={fadeUpItem} className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4 text-vokentra" />
          <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wider">
            Key Performance Indicators
          </h2>
          <div className="flex-1 h-px bg-white/[0.06] ml-2" />
        </motion.div>
        <KpiSummaryCards />
      </motion.section>

      {/* ── Section 2: Latency Metrics ──────────────────────────────── */}
      <motion.section
        variants={staggerContainer(0.1)}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={fadeUpItem} className="flex items-center gap-2 mb-3">
          <Activity className="w-4 h-4 text-success" />
          <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wider">
            Latency Metrics
          </h2>
          <div className="flex-1 h-px bg-white/[0.06] ml-2" />
        </motion.div>
        <LatencyDashboard
          sttLatency={sttLatency}
          agentLatency={agentLatency}
          ttsLatency={ttsLatency}
        />
      </motion.section>

      {/* ── Section 3: Cost Savings Simulation ──────────────────────── */}
      <motion.section
        variants={staggerContainer(0.1)}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={fadeUpItem} className="flex items-center gap-2 mb-3">
          <Activity className="w-4 h-4 text-info" />
          <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wider">
            Cost Analysis
          </h2>
          <div className="flex-1 h-px bg-white/[0.06] ml-2" />
        </motion.div>
        <CostSimulation />
      </motion.section>

      {/* ── Section 4: Deflection Rate Analytics ────────────────────── */}
      <motion.section
        variants={staggerContainer(0.1)}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={fadeUpItem} className="flex items-center gap-2 mb-3">
          <Activity className="w-4 h-4 text-warning" />
          <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wider">
            Deflection Analytics
          </h2>
          <div className="flex-1 h-px bg-white/[0.06] ml-2" />
        </motion.div>
        <DeflectionAnalytics
          deflectionTrend={deflectionTrend}
          issueDeflection={issueDeflection}
          funnel={funnel}
        />
      </motion.section>

      {/* ── Section 5 & 6: System Performance + Metrics Stream ─────── */}
      <motion.section
        variants={staggerContainer(0.1)}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 xl:grid-cols-3 gap-6"
      >
        {/* System Performance */}
        <div className="xl:col-span-2 space-y-3">
          <motion.div variants={fadeUpItem} className="flex items-center gap-2 mb-3">
            <Activity className="w-4 h-4 text-error" />
            <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wider">
              System Performance
            </h2>
            <div className="flex-1 h-px bg-white/[0.06] ml-2" />
          </motion.div>
          <SystemPerformance system={system} />
        </div>

        {/* Metrics Stream */}
        <div className="xl:col-span-1">
          <motion.div variants={fadeUpItem} className="flex items-center gap-2 mb-3">
            <Activity className="w-4 h-4 text-vokentra" />
            <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wider">
              Live Stream
            </h2>
            <div className="flex-1 h-px bg-white/[0.06] ml-2" />
          </motion.div>
          <MetricsStream events={callEvents} className="h-full" />
        </div>
      </motion.section>

      {/* ── Footer ──────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.5 }}
        className="flex items-center justify-between py-4 text-[10px] text-text-muted border-t border-white/[0.06]"
      >
        <span>
          Vokentra UK AI Voice Contact Center — KPI Analytics Dashboard
        </span>
        <span className="tabular-nums">
          Data refreshes every 2 seconds — Simulated for demonstration
        </span>
      </motion.div>
    </motion.div>
  );
}

export default Analytics;
